import { ref, computed, watch } from 'vue'
import useApiRequest from '@/_core/hooks/useApiRequest'
import useEventEmitter from '@/_core/hooks/useEventEmitter'
import { getChildVpr, getTaskProgress, getVprProgress, resetVprProgress } from '@/services/vpr'
import store from '@/store'
import { vprTaskProgressCacheClear } from '@/models/VprTask'

export default function ChildVprController () {
  const { $emit, $on, $off } = useEventEmitter()

  const childId = computed(() => store.user.selectedChild?.id)

  // Child vpr logic
  const vprId = ref(null)
  const setVprId = id => { vprId.value = id }

  const childVpr = ref(null)
  const _clearChildVpr = () => { childVpr.value = null }

  const requestChildVpr = useApiRequest(getChildVpr)
  requestChildVpr.$on('success', () => {
    childVpr.value = requestChildVpr.data
    $emit('requestChildVpr-success', requestChildVpr.data)
  })
  requestChildVpr.$on('error', () => {
    $emit('requestChildVpr-error', requestChildVpr.errors)
    _clearChildVpr()
  })
  const _childVprRequest = async (vprId) => {
    vprTaskProgressCacheClear()
    await requestChildVpr.request(childId.value, vprId)
  }

  watch([childId, vprId], ([_, vprId]) => _childVprRequest(vprId))

  // Child VPR progress state logic
  const requestChildVprState = useApiRequest(getVprProgress)
  requestChildVprState.$on('success', () => {
    childVpr.value.setStartworkProgress(JSON.parse(requestChildVprState.data.player_state))
    $emit('requestChildVprState-success', requestChildVprState.data)
  })
  requestChildVprState.$on('error', () => {
    $emit('requestChildVprState-error', requestChildVprState.errors)
  })
  const childVprStateRequest = async () => {
    await requestChildVprState.request(childId.value, vprId.value)
  }

  // Child VPR progress RESET state logic
  const requestChildVprReset = useApiRequest(resetVprProgress)
  requestChildVprReset.$on('success', () => {
    childVpr.value.setStartworkProgress(null)
    $emit('requestChildVprReset-success', requestChildVprReset.data)
  })
  requestChildVprReset.$on('error', () => {
    $emit('requestChildVprReset-error', requestChildVprReset.errors)
  })
  const resetVprStateRequest = async () => {
    await requestChildVprReset.request(childId.value, vprId.value)
  }

  // Child Task progress state logic
  const requestTaskState = useApiRequest(getTaskProgress)
  requestTaskState.$on('success', () => {
    childVpr.value.setTaskProgress(requestTaskState.data)
    $emit('requestTaskState-success', requestTaskState.data)
  })
  requestTaskState.$on('error', () => {
    $emit('requestTaskState-error', requestTaskState.errors)
  })
  const taskStateRequest = async (taskId) => {
    await requestTaskState.request(childId.value, vprId.value, taskId)
  }

  // Preload task state
  //    check if playerState exists, then request if it's needed
  const preloadTaskStateRequest = (task) => {
    if (!task) return
    if (!task?.progress?.preloaded) {
      taskStateRequest(task.id)
    }
  }

  // Output
  return {
    childVpr,
    setVprId,
    isLoading: computed(() => requestChildVpr.isLoading),
    childVprStateRequest,
    isVprStateLoading: computed(() => requestChildVprState.isLoading),
    isVprCompleted: computed(() => childVpr.value?.startworkProgress?.completed ?? false),
    resetVprStateRequest,
    isVprResetLoading: computed(() => requestChildVprReset.isLoading),
    taskStateRequest,
    isTaskLoading: computed(() => requestTaskState.isLoading),
    preloadTaskStateRequest,
    $on,
    $off
  }
}
